import {useCallback,useMemo} from 'react';
import {useAuth} from '../contexts/AuthContext';
import {useSettings} from '../contexts/SettingsContext';
import {hasModuleEntitlement,roleAllowsModule} from '../utils/entitlements';
import {canonicalModuleKey,canonicalModuleKeys,isCoreBusinessModule,CORE_BUSINESS_MODULES} from '../utils/moduleKeys';
import {normalizeInstitutionType} from '../config/institutionTypes';

const FEATURE_MODULE={
 admissions:'admissions',counselling:'admissions',students:'students',familyProfile:'students',
 academics:'academics',promotion:'academics',staff:'academics',attendance:'attendance',learning:'learning',
 exams:'exams',fees:'fees',finance:'finance',payroll:'payroll',documents:'documents',certificates:'documents',
 documentRequests:'documents',gatePass:'gatepass',live:'live',clinical:'clinical',transport:'transport',website:'website',
 library:'library',inventory:'inventory',uniform:'uniform',communication:'communication',notifications:'communication',whatsapp:'communication',
 internalTasks:'communication',reports:'reports',formbuilder:'formbuilder',behaviour:'behaviour',digitalcourses:'digitalcourses',supportChat:null,requisitions:null,
};

export default function useEntitlements(){
 const {profile,can}=useAuth();
 const {settings,refreshRuntime}=useSettings();
 const enabled=useMemo(()=>canonicalModuleKeys([...(settings.enabledModules||[]),...CORE_BUSINESS_MODULES]),[settings.enabledModules]);
 const roleModules=useMemo(()=>canonicalModuleKeys(profile?.moduleVisibility||[]),[profile?.moduleVisibility]);
 const institutionType=normalizeInstitutionType(settings.institutionType||settings.type);
 const catalog=useMemo(()=>Array.isArray(settings.moduleCatalogLive)?settings.moduleCatalogLive:[],[settings.moduleCatalogLive]);
 const catalogMap=useMemo(()=>new Map(catalog.map(row=>[canonicalModuleKey(row.key||row.id),row])),[catalog]);
 const packageModules=useMemo(()=>new Set(canonicalModuleKeys(settings.packageModuleKeys||[])),[settings.packageModuleKeys]);
 const activeAddons=useMemo(()=>new Set(canonicalModuleKeys(settings.addonModuleKeys||[])),[settings.addonModuleKeys]);
 const purchasedAddons=useMemo(()=>new Set(canonicalModuleKeys(settings.purchasedAddonModuleKeys||[])),[settings.purchasedAddonModuleKeys]);
 const moduleAllowed=useCallback((key)=>hasModuleEntitlement(enabled,key,settings.enforceModuleEntitlements)&&roleAllowsModule(roleModules,key),[enabled,roleModules,settings.enforceModuleEntitlements]);
 const featureAllowed=useCallback((feature)=>!feature||settings.moduleControls?.[feature]!==false,[settings.moduleControls]);
 const permissionAllowed=useCallback((permission)=>!permission||can(permission),[can]);
 const typeAllowed=useCallback((types)=>!Array.isArray(types)||!types.length||types.includes(institutionType),[institutionType]);
 const submoduleAllowed=useCallback((moduleKey,labelOrPath)=>{
   const row=catalogMap.get(canonicalModuleKey(moduleKey));
   if(!row||String(row.submoduleControlMode||'all')!=='selected')return true;
   const selected=Array.isArray(row.submodules)?row.submodules:[];
   if(!selected.length)return false;
   const norm=v=>String(v||'').toLowerCase().replace(/[^a-z0-9]+/g,' ').trim();
   const candidate=norm(labelOrPath);
   if(!candidate||candidate.includes('overview dashboard'))return true;
   const pathTail=norm(String(labelOrPath||'').split('?')[0].split('/').filter(Boolean).pop()||'');
   return selected.some(x=>{const n=norm(x);return !!n&&(n===candidate||candidate.includes(n)||n.includes(candidate)||n===pathTail||pathTail.includes(n)||n.includes(pathTail));});
 },[catalogMap]);
 const allowed=useCallback(({moduleKey,feature,permission,types}={})=>{
   const key=canonicalModuleKey(moduleKey||FEATURE_MODULE[feature]||'');
   return permissionAllowed(permission)&&featureAllowed(feature)&&typeAllowed(types)&&(!key||moduleAllowed(key));
 },[permissionAllowed,featureAllowed,typeAllowed,moduleAllowed]);
 const statusFor=useCallback((key)=>{
   const normalized=canonicalModuleKey(key);
   if(!normalized)return 'available';
   if(isCoreBusinessModule(normalized))return 'core';
   if(packageModules.has(normalized))return 'included';
   if(activeAddons.has(normalized))return 'active';
   if(purchasedAddons.has(normalized))return 'purchased';
   return moduleAllowed(normalized)?'enabled':'available';
 },[packageModules,activeAddons,purchasedAddons,moduleAllowed]);
 const isAddon=useCallback(key=>statusFor(key)==='active'&&!isCoreBusinessModule(key),[statusFor]);
 return {enabledModules:enabled,roleModules,institutionType,catalog,catalogMap,moduleAllowed,submoduleAllowed,featureAllowed,permissionAllowed,typeAllowed,allowed,statusFor,isAddon,refreshEntitlements:refreshRuntime,loading:!!settings.entitlementsLoading,runtimeLoaded:!!settings.runtimeConfigLoaded};
}

export {FEATURE_MODULE};
